import React, { createContext, useContext, useState, useEffect } from "react";
import { useGoldPrice } from "./GoldPriceContext";
import { calculateProductPrice } from "../utils/PriceUtils";

const CartContext = createContext();

export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const { goldPrice, makingFeePerGram } = useGoldPrice();
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      // Ensure finalPrice is a number
      const sanitizedCart = parsedCart.map((item) => ({
        ...item,
        finalPrice: Number(item.finalPrice) || 0,
      }));
      setCartItems(sanitizedCart);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    const { price, ...productWithoutPrice } = product; // Remove price to force dynamic calculation
    let finalPrice = Number(product.finalPrice);
    if (!finalPrice || isNaN(finalPrice)) {
      const weight = product.weight || product.goldWeight || 0;
      finalPrice = calculateProductPrice(
        weight,
        goldPrice,
        makingFeePerGram,
      ).totalPrice;
    }
    const productWithFinalPrice = { ...productWithoutPrice, finalPrice };
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item._id === product._id);
      const currentQuantity = existingItem ? existingItem.quantity : 0;

      // Check stock
      if (
        product.countInStock >= 0 &&
        currentQuantity + 1 > product.countInStock
      ) {
        console.warn("Cannot add more items, stock limit reached");
        return prevItems; // Don't add
      }

      if (existingItem) {
        return prevItems.map((item) =>
          item._id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      } else {
        return [...prevItems, { ...productWithFinalPrice, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item._id !== productId),
    );
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item._id === productId ? { ...item, quantity } : item,
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const weight = item.weight || item.goldWeight || 0;
      const itemPrice =
        item.finalPrice && !isNaN(Number(item.finalPrice))
          ? Number(item.finalPrice)
          : calculateProductPrice(weight, goldPrice, makingFeePerGram).totalPrice;
      return total + itemPrice * item.quantity;
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
